<template>
  <sidebar
    :addTitle="$t('GradeControl.Forecast.AddNew')"
    :editTitle="$t('GradeControl.Forecast.Edit')"
    :isSidebarActive="isSidebarActive"
    :data="data"
      @onNewData="newData"
      @onEditData="editData"
      @closeSidebar="closeSidebar">

    <ValidationObserver v-slot="{ handleSubmit }">
      <form-wizard
          ref="wizard"
          color="rgba(var(--vs-primary), 1)"
          errorColor="rgba(var(--vs-danger), 1)"
          :title="null"
          :subtitle="null"
          :nextButtonText="$t('Next')"
          :backButtonText="$t('Back')"
          :finishButtonText="$t('Submit')"
          @on-complete="handleSubmit(submitData)"
        >
        <tab-content :title="$t('GradeControl.Forecast.Step1')" class="mb-2" :before-change="validateStep1">
          <ValidationObserver ref="step1Observer" >
            <!-- Base scenario content -->
            <form data-vv-scope="step-1">
              <vs-row class="mb-3">
                <vs-col  vs-w="6">
                  <form-label>{{$t('StartDate')}}</form-label>
                  <strong class="ml-2">{{forecastStartDate | toDate}}</strong>
                </vs-col>
                <vs-col vs-w="6">
                  <form-label>{{$t('EndDate')}}</form-label>
                  <strong class="ml-2">{{forecastEndDate | toDate}}</strong>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col vs-w="7" class="mr-5">
                  <form-label>{{$t('GradeControl.Location.Type')}}</form-label>
                  <ValidationProvider :name="$t('GradeControl.Location.Type')" rules="required" v-slot="{ errors }">
                    <v-select
                      label="name"
                      :options="locationTypes"
                      v-model="forecastLocationType"
                      :reduce="locationType => locationType.id"
                      class="w-full"
                    />
                   <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col class="mt-10" vs-w="4">
                  <vs-checkbox v-model="onlyActiveLocation" class="mb-2">{{$t('GradeControl.Location.OnlyActive')}}</vs-checkbox>
                </vs-col>
              </vs-row>
              <vs-row class="mb-3">
                <vs-col  vs-w="12">
                  <form-label>{{$t('GradeControl.Location.Name')}}</form-label>
                   <ValidationProvider :name="$t('GradeControl.Location.Name')" rules="required" v-slot="{ errors }">
                    <v-select
                      label="code"
                      :options="filteredlocations"
                      :reduce="location => location.locationId"
                      v-model="forecastLocationId"
                      class="w-full"
                    />
                     <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-5" vs-type="flex" vs-justify="space-between">
                <vs-col vs-w="6" class="mr-10" v-if="useBucketInput">
                  <ValidationProvider :name="$t('GradeControl.Density')" rules="required|decimal" v-slot="{ errors }">
                    <validated-input
                      :label="$systemUnits.getDensityLabel()"
                      v-model="forecastDensity"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6">
                  <ValidationProvider :name="$t('GradeControl.Forecast.Tonnage')" rules="required|decimal|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$systemUnits.getTonnageLabel()"
                      v-model="forecastTonnage"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
            </form>
          </ValidationObserver>
        </tab-content>
        <tab-content :title="$t('GradeControl.Forecast.Step2')" class="mb-2" :before-change="validateStep2">
          <ValidationObserver ref="step2Observer" >
            <!-- Gold content -->
            <form data-vv-scope="step-2">
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left" class="mb-2">{{ $systemUnits.getGradeLabel('Gold') }}</vs-divider>
                <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('GradeControl.Grade')" rules="required|decimal|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Grade')"
                      v-model="forecastGoldGrade"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6">
                  <ValidationProvider :name="$t('GradeControl.Cutoff')" rules="numeric" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Cutoff')"
                      v-model="forecastGoldCutoff"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left" class="mb-2">{{ $systemUnits.getGradeLabel('Silver') }}</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('GradeControl.Grade')" rules="decimal|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Grade')"
                      v-model="forecastSilverGrade"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6">
                  <ValidationProvider :name="$t('GradeControl.Cutoff')" rules="numeric" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Cutoff')"
                      v-model="forecastSilverCutoff"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left" class="mb-2">{{$t('Copper')}} (%)</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('GradeControl.Grade')" rules="decimal:2|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Grade')"
                      v-model="forecastCopperGrade"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
               <vs-col vs-w="6">
                  <ValidationProvider :name="$t('GradeControl.Cutoff')" rules="numeric" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Cutoff')"
                      v-model="forecastCopperCutoff"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left" class="mb-2">{{$t('Zinc')}} (%)</vs-divider>
                <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('GradeControl.Grade')" rules="decimal:2|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Grade')"
                      v-model="forecastZincGrade"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6">
                  <ValidationProvider :name="$t('GradeControl.Cutoff')" rules="numeric" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Cutoff')"
                      v-model="forecastZincCutoff"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
              <vs-row class="mb-1" vs-type="flex" vs-justify="space-between">
                <vs-divider position="left" class="mb-2">{{$t('Lead')}} (%)</vs-divider>
                  <vs-col vs-w="5" class="mr-5">
                  <ValidationProvider :name="$t('GradeControl.Grade')" rules="decimal:2|min_value:0" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Grade')"
                      v-model="forecastLeadGrade"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
                <vs-col vs-w="6">
                  <ValidationProvider :name="$t('GradeControl.Cutoff')" rules="numeric" v-slot="{ errors }">
                    <validated-input
                      :label="$t('GradeControl.Cutoff')"
                      v-model="forecastLeadCutoff"
                    ></validated-input>
                    <validation-message
                      >{{ errors[0] }}</validation-message>
                  </ValidationProvider>
                </vs-col>
              </vs-row>
            </form>
          </ValidationObserver>
        </tab-content>
      </form-wizard>
    </ValidationObserver>
  </sidebar>
</template>

<script>
import { FormWizard, TabContent } from "vue-form-wizard";
import Datepicker from 'vuejs-datepicker';
import vSelect from 'vue-select'
import {
  FormLabel,
  ValidatedInput,
  ValidationMessage,
  Sidebar
} from "./../../../../shared/components";
import LocationTypeEnum from "../../LocationTypeEnum.js";

export default {
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true
    },
    initialDate: {
      type: Date,
      required: false
    },
    locations: {
      type: Array,
      required: true
    },
    data: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    FormWizard,
    TabContent,
    Sidebar,
    'v-select': vSelect,
    Datepicker,
    FormLabel,
    ValidatedInput,
    ValidationMessage
  },
  data() {
    return {
      onlyActiveLocation : true,
      forecastId: 0,
      forecastStartDate: new Date(),
      forecastEndDate: new Date(),
      forecastLocationType: null,
      forecastLocationId: null,
      forecastDensity: null,
      forecastTonnage: null,
      forecastGoldForecastResultId: 0,
      forecastGoldGrade: null,
      forecastGoldCutoff: null,
      forecastSilverForecastResultId: 0,
      forecastSilverGrade: null,
      forecastSilverCutoff: null,
      forecastCopperForecastResultId: 0,
      forecastCopperGrade: null,
      forecastCopperCutoff: null,
      forecastZincForecastResultId: 0,
      forecastZincGrade: null,
      forecastZincCutoff: null,
      forecastLeadForecastResultId: 0,
      forecastLeadGrade: null,
      forecastLeadCutoff: null,
      locationTypes: LocationTypeEnum.definition
    };
  },
  watch: {
    forecastLocationId(val) {
      if (!val) {
        return;
      }
      if (this.useBucketInput) {        
        if (this.forecastDensity === null || this.forecastDensity === '' || this.forecastDensity === 0) {
          this.forecastDensity = this.filteredlocations.find(l => l.locationId === val).density;
        }
      }
    }
  },
  computed: {
    isSidebarActiveLocal: {
      get() {
        return this.isSidebarActive;
      },
      set(val) {
        if (!val) {
          this.$emit("closeSidebar");
        }
      }
    },
    isFormValid() {
      return !this.errors.any();
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    },
    filteredlocations() {
      if (this.onlyActiveLocation) {
        return this.locations.filter(l => l.type === this.forecastLocationType && l.isActive === true);
      } else {
        return this.locations.filter(l => l.type === this.forecastLocationType);
      }
    },
    useBucketInput() {
      return this.$store.getters.isProjectUsingBucketInput;
    }
  },
  methods: {
    newData() {
      this.initValues();
      if (this.initialDate !== null)
      {
        this.forecastStartDate = this.initialDate;

        const d = new Date(this.initialDate.valueOf())
        d.setMonth(d.getMonth() + 1);
        if (d.getDate() !== d) {
          d.setDate(0);
        }

        this.forecastEndDate = d;
        if (!this.useBucketInput) {
          // Setting it to a non-null value so the backend does not trip on validation.
          // Will be fed the default project density on the backend side.
          // - Aub 2026-01-05
          this.forecastDensity = 0;
        }
      }
      this.$refs.wizard.reset();
    },
    editData() {
      const {
        locationForecastId,
        startDate, 
        endDate, 
        locationId,
        density,
        tonnage,
        results} = JSON.parse(JSON.stringify(this.data));
      this.forecastId = locationForecastId
      this.forecastStartDate = startDate;
      this.forecastEndDate = endDate;
      this.forecastLocationId = locationId;
      const location = this.locations.find(l => l.locationId === locationId)
      if (location) {
        this.forecastLocationType = location.type;
      }
            
      this.forecastDensity = this.$systemUnits.convertDensity(density);
      this.forecastTonnage = this.$systemUnits.convertTonnage(tonnage);
      this.forecastGoldForecastResultId = this.getElementId(results, 0);
      this.forecastGoldGrade = this.getGradeForecast(results, 0);
      this.forecastGoldCutoff =  this.getCutoff(results, 0);
      this.forecastSilverForecastResultId = this.getElementId(results, 1);
      this.forecastSilverGrade = this.getGradeForecast(results, 1);
      this.forecastSilverCutoff =  this.getCutoff(results, 1);
      this.forecastCopperForecastResultId = this.getElementId(results, 2);
      this.forecastCopperGrade = this.getGradeForecast(results, 2);
      this.forecastCopperCutoff =  this.getCutoff(results, 2);
      this.forecastZincForecastResultId = this.getElementId(results, 3);
      this.forecastZincGrade = this.getGradeForecast(results, 3);
      this.forecastZincCutoff =  this.getCutoff(results, 3);
      this.forecastLeadForecastResultId = this.getElementId(results, 4);
      this.forecastLeadGrade = this.getGradeForecast(results, 4);
      this.forecastLeadCutoff =  this.getCutoff(results, 4);

      const selectedLocation = this.locations.find(e => e.locationId === locationId);
      if (selectedLocation) {
        this.onlyActiveLocation = selectedLocation.isActive;
      } else {
        this.onlyActiveLocation = true;
      }


      this.$refs.wizard.reset();
    },
    closeSidebar() {
      this.$emit("closeSidebar");
    },
    initValues() {
      if (this.data.id) return;
        
      this.forecastId = 0;
      this.forecastStartDate = null;
      this.forecastEndDate = null;
      this.forecastLocationType = null;
      this.forecastLocationId = null;
      if (this.useBucketInput) {
        this.forecastDensity = 0;      
      }
      else {
        this.forecastDensity = this.projectGradeControlDefaultDensity;
      }
      this.forecastTonnage = 0;
      this.forecastGoldForecastResultId = 0;
      this.forecastGoldGrade = null;
      this.forecastGoldCutoff = null;
      this.forecastSilverForecastResultId = 0;
      this.forecastSilverGrade = null;
      this.forecastSilverCutoff = null;
      this.forecastCopperForecastResultId = 0;
      this.forecastCopperGrade = null;
      this.forecastCopperCutoff = null;
      this.forecastZincForecastResultId = 0;
      this.forecastZincGrade = null;
      this.forecastZincCutoff = null;
      this.forecastLeadForecastResultId = 0;
      this.forecastLeadGrade = null;
      this.forecastLeadCutoff = null;
      this.onlyActiveLocation  = true;
    },
    
    getElementId(results, elementId) {
      if (results !== null) {
        const element = results.find(r => r.element === elementId)
        if (element) {
          return element.forecastResultId;
        }
        else {
          return 0;
        }
      }
      else {
        return 0;
      }
    },
    getGradeForecast(results, elementId) {
      if (results !== null) {
        const element = results.find(r => r.element === elementId)
        if (element) {
          return this.$systemUnits.convertGrade(element.grade, elementId);
        }
        else {
          return null;
        }
      }
      else {
        return null;
      }
    },
    getCutoff(results, elementId) {
      if (results !== null) {
        const element = results.find(r => r.element === elementId)
        if (element) {
          return this.$systemUnits.convertGrade(element.cutOff, elementId);
        }
        else {
          return null;
        }
      }
      else {
        return null;
      }
    },

    defineResult(element, locationForecastId, forecastResultId, grade, cutoff, results) {
      if (grade !== null && grade !== '') {
        results.push({
          locationForecastId,
          forecastResultId,
          element,
          grade: this.$systemUnits.convertGradeToMetric(grade, element),
          cutoff: this.$systemUnits.convertGradeToMetric(cutoff, element)
        })
      }
    },
    validateStep1() {
      return new Promise((resolve, reject) => {
        this.$refs.step1Observer.validate().then(result => {
          if (result) {
            resolve(true);
          } else {
            reject("correct all values");
          }
        });
      });
    },
    validateStep2() {
      return new Promise((resolve, reject) => {
        this.$refs.step2Observer.validate().then(result => {
          if (result) {
            resolve(true);
          } else {
            reject("correct all values");
          }
        });
      });
    },
    submitData() {
      const obj = {
        locationForecastId: this.forecastId,
        locationId: this.forecastLocationId,
        density: this.$systemUnits.convertDensityToMetric(this.forecastDensity),
        tonnage: this.$systemUnits.convertTonnageToMetric(this.forecastTonnage),
        results: []
      };
      
      if (Object.prototype.toString.call(this.forecastStartDate) === "[object Date]") {
        obj.startDate = this.$options.filters.toDate(this.forecastStartDate);
      }
      else {
        obj.startDate = this.forecastStartDate;
      }

      if (Object.prototype.toString.call(this.forecastEndDate) === "[object Date]") {
        obj.endDate = this.$options.filters.toDate(this.forecastEndDate);
      }
      else {
        obj.endDate = this.forecastEndDate;
      }

      this.defineResult(0, this.forecastId, this.forecastGoldForecastResultId, this.forecastGoldGrade, this.forecastGoldCutoff, obj.results);
      this.defineResult(1, this.forecastId, this.forecastSilverForecastResultId, this.forecastSilverGrade, this.forecastSilverCutoff, obj.results);
      this.defineResult(2, this.forecastId, this.forecastCopperForecastResultId, this.forecastCopperGrade, this.forecastCopperCutoff, obj.results);
      this.defineResult(3, this.forecastId, this.forecastZincForecastResultId, this.forecastZincGrade, this.forecastZincCutoff, obj.results);
      this.defineResult(4, this.forecastId, this.forecastLeadForecastResultId, this.forecastLeadGrade, this.forecastLeadCutoff, obj.results);

      this.$vs.loading();
      if (this.forecastId !== null && this.forecastId > 0) {
        this.$store.dispatch("gradeControl/updateForecast", obj).then((result) => {
          this.$emit("onForecastUpdated", result.data);
        }).finally(() => {
          this.$vs.loading.close();
        });
      } else {
        this.$store.dispatch("gradeControl/addForecast", obj).then((result) => {
          this.$emit("onForecastInserted", result.data);
        }).finally(() => {
          this.$vs.loading.close();
        });
      }
    }
  }
};
</script>

